import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { isOffline, isOfflinePlayReady, onConnectivityChange } from '../services/pwa.js';

/**
 * The home hero's "Play offline" call-to-action. Offline play needs its engine cached, so this link is
 * only safe to offer when it will actually work:
 *   - Online: shown — clicking loads the engine over the network (and caches it for later).
 *   - Offline + engine ready (installed PWA, or a tab that cached it on a previous online /offline visit): shown.
 *   - Offline + engine NOT ready: hidden — the link would open /offline and 404 on the uncached engine.
 *
 * Rendered into the light DOM so the page's global button styles apply. The server emits a plain
 * <a class="btn-..."> as the element's child (the no-JS fallback); this component replaces it once it
 * upgrades and then manages its visibility live as connectivity changes.
 */
@customElement('gc-play-offline')
export class GcPlayOffline extends LitElement {
  /** "primary" or "ghost" — selects the matching global button style. */
  @property() variant = 'ghost';

  @state() private offline = isOffline();
  @state() private ready = false;
  private unsubscribe?: () => void;

  /** Light DOM (so global .btn-* styles apply); clear the SSR no-JS fallback to avoid a duplicate link. */
  protected override createRenderRoot(): HTMLElement {
    this.replaceChildren();
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.offline = isOffline();
    void this.refreshReady();
    this.unsubscribe = onConnectivityChange((online) => {
      this.offline = !online;
      void this.refreshReady();
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.unsubscribe?.();
  }

  private async refreshReady(): Promise<void> {
    // Readiness must reflect whether the engine is genuinely cached for this app version — NOT merely
    // that we're installed. An installed PWA whose engine hasn't been cached yet (fresh install, or an
    // app update that invalidated the cache before the next online launch re-cached it) would otherwise
    // wrongly keep the "Play offline" link, which 404s on the uncached engine. Consult the real marker.
    this.ready = await isOfflinePlayReady();
  }

  override render() {
    // Hide only when offline AND the engine isn't cached — that's the one case where the link breaks.
    if (this.offline && !this.ready) {
      return nothing;
    }
    const cls = this.variant === 'primary' ? 'btn-primary' : 'btn-ghost';
    return html`<a class=${cls} href="/offline">Play offline</a>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gc-play-offline': GcPlayOffline;
  }
}
